import { useState, useRef, useCallback, useEffect } from 'react';

interface PullToRefreshConfig {
  onRefresh: () => Promise<void>;
  threshold?: number;
  maxPull?: number;
}

interface PullToRefreshReturn {
  pullDistance: number;
  isRefreshing: boolean;
  isPulling: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  pullIndicatorStyle: React.CSSProperties;
}

export function usePullToRefresh({
  onRefresh,
  threshold = 80,
  maxPull = 120,
}: PullToRefreshConfig): PullToRefreshReturn {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const pullDistanceRef = useRef(0);
  const isPullingRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const rafId = useRef(0);

  // Keep refs in sync with state
  useEffect(() => { isRefreshingRef.current = isRefreshing; }, [isRefreshing]);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const el = containerRef.current;
    if (!el || el.scrollTop > 0 || isRefreshingRef.current) return;
    startY.current = e.touches[0].clientY;
    isPullingRef.current = true;
    setIsPulling(true);
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isPullingRef.current || isRefreshingRef.current) return;
    const diff = e.touches[0].clientY - startY.current;

    if (diff > 0) {
      const resistance = Math.min(diff * 0.4, maxPull);
      pullDistanceRef.current = resistance;

      // Use RAF to batch DOM updates instead of triggering React re-renders
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        setPullDistance(resistance);
      });
    }
  }, [maxPull]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPullingRef.current) return;
    isPullingRef.current = false;
    setIsPulling(false);
    cancelAnimationFrame(rafId.current);

    if (pullDistanceRef.current >= threshold && !isRefreshingRef.current) {
      setIsRefreshing(true);
      setPullDistance(threshold * 0.6);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
        pullDistanceRef.current = 0;
      }
    } else {
      setPullDistance(0);
      pullDistanceRef.current = 0;
    }
  }, [threshold, onRefresh]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
      cancelAnimationFrame(rafId.current);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const pullIndicatorStyle: React.CSSProperties = {
    transform: `translateY(${pullDistance}px)`,
    transition: isPulling ? 'none' : 'transform 0.3s ease-out',
  };

  return {
    pullDistance,
    isRefreshing,
    isPulling,
    containerRef,
    pullIndicatorStyle,
  };
}
